import { DrawerContext } from '@components/drawer/useDrawer';
import { isClosedPolygon } from '@components/FramePolygonSized';
import { GeoZoneCoordinates } from '@EcamModel/model';
import Konva from 'konva';
import { useContext, useEffect, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { GeoZonePoints } from '../details/components/AddGeoZones';
import { Mode } from '../details/components/CameraInformation';
import { FramePolygonProps } from '../details/components/tabsGeozonesDetail/components/FramePolygonGeoOverview';
import { PREVIEW_RATIO } from './CameraDetailsProvider';

export default function useFramePolygonGeoZones(props: FramePolygonProps) {
    const {
        mode,
        imageElement,
        prevGeoZone,
        selectedGeoZone,
        onChange,
        onDelete,
        multiPolygon,
        onChangeMulti,
        onChangeCurrent,
    } = props;
    const { open } = useContext(DrawerContext);

    const containerRef = useRef<HTMLDivElement | null>(null);
    const [containerSize, setContainerSize] = useState({
        width: window.innerWidth,
        height: window.innerWidth * PREVIEW_RATIO,
    });

    const stageRef = useRef<Konva.Stage>(null);
    const shapeRef = useRef<Konva.Shape>(null);

    const scaleSize = containerSize.width / (imageElement?.width ?? 1920);

    const scaledPrevGeoZone = prevGeoZone?.map((frame) =>
        frame?.map((point) => ({
            x: point?.XPoint * scaleSize,
            y: point?.YPoint * scaleSize,
            geoZoneId: point.GeoZoneId,
        }))
    );

    const isPointInsideFrame = (point: Partial<GeoZoneCoordinates>, frame: GeoZoneCoordinates[]) => {
        var x = point.XPoint,
            y = point.YPoint;
        var inside = false;
        for (var i = 0, j = frame.length - 1; i < frame.length; j = i++) {
            var xi = frame[i]?.XPoint,
                yi = frame[i]?.YPoint;
            var xj = frame[j]?.XPoint,
                yj = frame[j]?.YPoint;
            var intersect = yi > y! !== yj > y! && x! < ((xj - xi) * (y! - yi)) / (yj - yi) + xi;
            if (intersect) inside = !inside;
        }
        return inside;
    };

    const isPrevCurrentGzInsideFrame = (point: GeoZoneCoordinates, excludedId?: string) => {
        return (
            prevGeoZone?.some((frame) => isPointInsideFrame(point, frame)) ||
            multiPoints?.some((polygon) => polygon.id !== excludedId && isPointInsideFrame(point, polygon.points))
        );
    };

    const [multiPoints, setMultiPoints] = useState<GeoZonePoints[]>([]);
    const [currentPoints, setCurrentPoints] = useState<GeoZonePoints>({ id: uuidv4(), points: [] });
    const [points, setPoints] = useState<GeoZoneCoordinates[]>([]);
    const [isDrag, setIsDrag] = useState<boolean>(false);

    const activePoints = multiPolygon ? currentPoints.points : points;
    const setActivePoints = multiPolygon
        ? (newPoints: GeoZoneCoordinates[]) => setCurrentPoints((prev) => ({ ...prev, points: newPoints }))
        : setPoints;

    const handleDrawFrame = (e: any) => {
        const isPointsClosed = isClosedPolygon(activePoints);

        if (!multiPolygon && isPointsClosed) {
            return;
        }

        const stage = stageRef.current;
        if (!stage) return;

        const pointer = stage.getPointerPosition();
        if (!pointer) return;

        const currentScale = stage.scaleX(); // Lấy tỷ lệ zoom hiện tại
        const clickedX = pointer.x / currentScale / scaleSize;
        const clickedY = pointer.y / currentScale / scaleSize;

        const polygonIndex = multiPoints.findIndex((polygon) =>
            isPointInsideFrame({ XPoint: clickedX, YPoint: clickedY }, polygon.points)
        );
        if (polygonIndex !== -1) {
            handleSelectPolygon(polygonIndex);
            return;
        }

        if (isPointsClosed) {
            const newPoint: GeoZoneCoordinates = {
                XPoint: clickedX,
                YPoint: clickedY,
                Index: 1,
                GeoZoneId: selectedGeoZone?.GeoZoneCoordinates?.[0].GeoZoneId,
            };
            if (prevGeoZone?.some((frame) => isPointInsideFrame(newPoint, frame))) {
                return;
            }
            setCurrentPoints((prev) => ({ ...prev, id: uuidv4(), points: [newPoint] }));
            return;
        }

        if (activePoints.length >= 3) {
            const firstPoint = activePoints[0];
            const dist = Math.hypot(firstPoint.XPoint - clickedX, firstPoint.YPoint - clickedY);
            if (dist < 10 / scaleSize) {
                const closedPolygon = [...activePoints, { ...firstPoint }];
                if (multiPolygon) {
                    const _newPoints = { id: uuidv4(), points: closedPolygon };
                    setMultiPoints((prev) => [...prev, _newPoints]);
                    setCurrentPoints((prev) => ({ ...prev, id: _newPoints.id, points: closedPolygon }));
                } else {
                    setPoints(closedPolygon);
                }
                return;
            }
        }

        const newPoint: GeoZoneCoordinates = {
            XPoint: clickedX,
            YPoint: clickedY,
            Index: activePoints.length + 1,
            GeoZoneId: selectedGeoZone?.GeoZoneCoordinates?.[0].GeoZoneId,
        };

        if (prevGeoZone?.some((frame) => isPointInsideFrame(newPoint, frame))) {
            return;
        }

        if (multiPolygon) {
            setCurrentPoints((prev) => ({
                ...prev,
                points: [...prev.points, newPoint],
            }));
        } else {
            setPoints((prev) => [...prev, newPoint]);
        }
    };

    const handleDragAnchors = (index: number, e: any) => {
        const newPoints: GeoZoneCoordinates[] = [...activePoints];

        const newPoint: GeoZoneCoordinates = {
            XPoint: e.target.x() / scaleSize,
            YPoint: e.target.y() / scaleSize,
            Index: activePoints[index].Index,
            GeoZoneId: selectedGeoZone?.GeoZoneCoordinates?.[0].GeoZoneId,
        };

        // ❌ Không cho phép chồng lên vùng cũ
        if (isPrevCurrentGzInsideFrame(newPoint, currentPoints?.id)) {
            e.target.to({
                x: activePoints[index].XPoint * scaleSize,
                y: activePoints[index].YPoint * scaleSize,
            });
            return;
        }

        const isClosed = isClosedPolygon(activePoints);

        const isFirstPoint = index === 0;
        const isLastPoint = index === activePoints.length - 1;

        // Nếu hình đã đóng và đang kéo điểm đầu hoặc điểm cuối → đồng bộ 2 điểm
        if (isClosed && (isFirstPoint || isLastPoint)) {
            newPoints[0] = { ...newPoint };
            newPoints[activePoints.length - 1] = { ...newPoint };
            setActivePoints(newPoints);
            return;
        }

        // Nếu chưa đóng và kéo gần điểm đầu thì đóng lại
        const isClosingCandidate =
            activePoints.length >= 3 &&
            isLastPoint &&
            Math.hypot(newPoint.XPoint - activePoints[0].XPoint, newPoint.YPoint - activePoints[0].YPoint) <
                5 / scaleSize;

        if (isClosingCandidate) {
            const closedPolygon = [...activePoints.slice(0, index), { ...activePoints[0] }];

            if (multiPolygon) {
                const _newPoints = { id: uuidv4(), points: closedPolygon };
                setMultiPoints((prev) => [...prev, _newPoints]);
                setCurrentPoints((prev) => ({ ...prev, id: _newPoints.id, points: closedPolygon }));
            } else {
                setPoints(closedPolygon);
            }
            return;
        }

        newPoints[index] = newPoint;
        setActivePoints(newPoints);
    };

    const handleDragShape = (e: any, polygonIndex?: number) => {
        const shapeNode = e.target;

        if (shapeNode) {
            const dx = shapeNode.x() / scaleSize;
            const dy = shapeNode.y() / scaleSize;

            if (typeof polygonIndex === 'number') {
                // Dragging an existing polygon from multiPoints
                const updatedPolygon = multiPoints[polygonIndex].points.map((point) => ({
                    ...point,
                    XPoint: point.XPoint + dx,
                    YPoint: point.YPoint + dy,
                }));

                const shapeInsideFrame = updatedPolygon.some((point) =>
                    isPrevCurrentGzInsideFrame(point, multiPoints[polygonIndex]?.id)
                );

                if (shapeInsideFrame) {
                    shapeNode.position({ x: 0, y: 0 });
                    setIsDrag(false);
                    return;
                }
                const updatedAllPoints = [...multiPoints];
                updatedAllPoints[polygonIndex] = { ...updatedAllPoints[polygonIndex], points: updatedPolygon };
                setMultiPoints(updatedAllPoints);
            } else {
                // Dragging the currently drawing shape
                const updatedPoints = activePoints.map((point) => ({
                    ...point,
                    XPoint: point.XPoint + dx,
                    YPoint: point.YPoint + dy,
                }));

                const shapeInsideFrame = updatedPoints.some((point) =>
                    isPrevCurrentGzInsideFrame(point, currentPoints?.id)
                );

                if (shapeInsideFrame) {
                    shapeNode.position({ x: 0, y: 0 });
                    setIsDrag(false);
                    return;
                }
                setActivePoints(updatedPoints);
            }

            shapeNode.position({ x: 0, y: 0 });
            setIsDrag(false);
        }
    };

    const handleSelectPolygon = (polygonIndex: number) => {
        const newmultiPoints = [...multiPoints];
        const selectedPolygon = newmultiPoints.splice(polygonIndex, 1)[0];

        setCurrentPoints(selectedPolygon);
    };

    const handleDeleteLastPolygon = () => {
        if (multiPolygon) {
            if (activePoints.length > 0) {
                setMultiPoints((prev) => {
                    const newList = prev.filter((item) => item.id !== currentPoints.id);
                    setCurrentPoints(newList.length > 0 ? newList[newList.length - 1] : { id: uuidv4(), points: [] });
                    return newList;
                });
            }
        } else {
            setPoints([]);
            onDelete?.([]);
        }
    };

    const updateMultiPointById = (id: string, data: Partial<GeoZonePoints>) => {
        setMultiPoints((prev) => prev.map((item) => (item.id === id ? { ...item, ...data } : item)));
    };

    useEffect(() => {
        const isEdit = mode === Mode.edit;
        if (isEdit && isClosedPolygon(activePoints)) {
            multiPolygon ? onChangeCurrent?.(currentPoints) : onChange?.(activePoints);
        } else if (!isEdit) {
            multiPolygon ? onChangeCurrent?.(currentPoints) : onChange?.(activePoints);
        }
    }, [mode, activePoints, setCurrentPoints]);

    useEffect(() => {
        multiPolygon && onChangeMulti?.(multiPoints);
    }, [multiPoints]);

    useEffect(() => {
        if (currentPoints?.points?.length && multiPolygon) {
            setMultiPoints((prev) => prev.map((item) => (item.id === currentPoints.id ? { ...currentPoints } : item)));
        }
    }, [currentPoints]);

    useEffect(() => {
        if (!selectedGeoZone?.GeoZoneCoordinates) {
            let initialPolygon: GeoZoneCoordinates[] = [];
            setActivePoints(initialPolygon);
            setMultiPoints([]);
        } else {
            setActivePoints(
                selectedGeoZone?.GeoZoneCoordinates?.concat(selectedGeoZone?.GeoZoneCoordinates?.[0]) || []
            );
        }
    }, [selectedGeoZone?.GeoZoneCoordinates?.[0].GeoZoneId]);

    useEffect(() => {
        const updateStageSize = () => {
            if (containerRef.current) {
                const width = containerRef.current.offsetWidth;
                const height = width * PREVIEW_RATIO;
                setContainerSize({ width, height });
            } else {
                setTimeout(updateStageSize, 300);
            }
        };

        if (open) {
            setTimeout(updateStageSize, 300);
        } else {
            updateStageSize();
        }
        window.addEventListener('resize', updateStageSize);

        return () => {
            window.removeEventListener('resize', updateStageSize);
        };
    }, [open]);

    return {
        stageRef,
        shapeRef,
        points: activePoints,
        multiPoints,
        isDrag,
        scaledPrevGeoZone,
        scaleSize,
        prevGeoZone,
        selectedGeoZone,
        imageElement,
        containerRef,
        containerSize,
        currentPoints,
        setPoints: setActivePoints,
        handleDrawFrame,
        handleDragAnchors,
        handleDragShape,
        handleDeleteLastPolygon,
        handleSelectPolygon,
        setIsDrag,
        updateMultiPointById,
        isPrevCurrentGzInsideFrame,
    };
}
